/**
 * EmptyState — qué se muestra cuando una lista no tiene nada.
 *
 * ⚠️ Convive con `components/EmptyState.tsx`, que usan 25 archivos. La
 * diferencia que justifica el port son las **tres variantes**: «no hay nada
 * todavía», «el filtro no devolvió nada» y «no se pudo cargar» son tres
 * situaciones distintas y el del repo las trata igual. La segunda es la que más
 * importa: un listado vacío por un filtro se lee como un sistema sin datos, y
 * la salida es limpiar el filtro, no crear un registro.
 *
 * ⚠️ El anuncio va en dos niveles y los dos hacen falta: `role="alert"` en la
 * variante de **error**, que interrumpe, y `role="status"` con
 * `aria-live="polite"` en las otras dos, que espera a que el lector termine lo
 * que está diciendo.
 *
 * 🔴 El port perdió ese segundo nivel —el sistema solo marcaba el error— y es
 * justamente el caso frecuente: cuando una tabla se vacía al aplicar un filtro,
 * sin `aria-live` quien usa lector de pantalla **no se entera de que la lista
 * cambió**. El `EmptyState` del repo sí lo tenía, así que migrar sin esto habría
 * sido una regresión para los 25 archivos que ya lo usan. Medido: **56 de los 58
 * estados vacíos escritos a mano no anuncian nada**.
 *
 * ⚠️ Los textos por defecto están reescritos: los del sistema usan segunda
 * persona («Prueba con otro rango», «Vuelve a intentarlo»), que la §4bis del
 * CLAUDE.md descarta por construcción — en Argentina el registro estándar es el
 * voseo y en Chile el tuteo es informal, así que cualquier conjugación suena
 * ajena en al menos un mercado.
 */
import type { CSSProperties, HTMLAttributes, ReactNode } from 'react'

export type EmptyStateVariant = 'empty' | 'filtered' | 'error'

export interface EmptyStateProps
  extends Omit<HTMLAttributes<HTMLDivElement>, 'className' | 'style' | 'title'> {
  variant?: EmptyStateVariant
  icon?: ReactNode
  title?: ReactNode
  description?: ReactNode
  /** El botón que resuelve la situación: crear, limpiar el filtro, reintentar. */
  action?: ReactNode
  className?: string
  style?: CSSProperties
}

const POR_DEFECTO: Record<EmptyStateVariant, { title: string; description: string }> = {
  empty: {
    title: 'Todavía no hay nada acá',
    description: 'Al crear el primer registro va a aparecer en esta lista.',
  },
  filtered: {
    title: 'Sin resultados para este filtro',
    description: 'Conviene probar con otro rango de fechas o limpiar los filtros aplicados.',
  },
  error: {
    title: 'No se pudo cargar la información',
    description: 'Vale la pena reintentar. Si el problema sigue, avisar a soporte.',
  },
}

export function EmptyState({
  variant = 'empty',
  icon,
  title,
  description,
  action,
  className = '',
  style,
  ...rest
}: EmptyStateProps) {
  const d = POR_DEFECTO[variant] ?? POR_DEFECTO.empty

  return (
    <div
      className={className}
      role={variant === 'error' ? 'alert' : 'status'}
      aria-live={variant === 'error' ? undefined : 'polite'}
      style={{
        padding: '38px 24px',
        textAlign: 'center',
        maxWidth: 380,
        margin: '0 auto',
        ...style,
      }}
      {...rest}
    >
      <div
        className="cl-surface-3"
        aria-hidden="true"
        style={{
          width: 40,
          height: 40,
          borderRadius: 13,
          margin: '0 auto',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'rgb(var(--color-content-subtle))',
        }}
      >
        {icon || (
          <svg
            width="19"
            height="19"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
          >
            <rect x="4" y="4" width="16" height="16" rx="4" />
            <line x1="9" y1="12" x2="15" y2="12" />
          </svg>
        )}
      </div>
      <div style={{ fontSize: 13.5, fontWeight: 700, marginTop: 13 }}>{title || d.title}</div>
      <div
        style={{
          fontSize: 12,
          color: 'rgb(var(--color-content-muted))',
          marginTop: 6,
          lineHeight: 1.55,
        }}
      >
        {description || d.description}
      </div>
      {action && (
        <div style={{ marginTop: 16, display: 'flex', justifyContent: 'center' }}>{action}</div>
      )}
    </div>
  )
}
